import { sortBy } from 'lodash';
import {
  getStopsPropertiesForRecommendation,
  getDriverAssignmentsList,
  getEstimatedMilesRemaining,
  getEstimatedTravelTime,
  getArriveAtTerminalTime,
  formatRecommendedArriveAtTerminalTime
} from '../../../common/utils';
import { RMAXLogger } from '@RMAXLogger';

/** Virtual stop field from getDispatchRouteRecommendations — pallets left before assigning this pickup */
export const ESTIMATED_TRAILER_SPACE_BEFORE_PICKUP_ASSIGNMENT_FIELD =
  'estimatedTrailerSpaceRemainingBeforePickupAssignment';

const getMaxSkidFromRoute = (matchedRoute) => {
  return matchedRoute?.trailer?.maxSkid;
};

const formatPalletsLeftKpi = (trailerCapacity) => {
  if (!trailerCapacity) {
    return undefined;
  }
  return trailerCapacity;
};

const getRecommendationTrailerCapacityDetails = ({ stops, matchedRoute, routeId, driverId }) => {
  const fallbackMaxSkid = getMaxSkidFromRoute(matchedRoute);
  const virtualStop = stops.find((stop) => stop.isVirtualStop);

  if (!virtualStop) {
    RMAXLogger.info('ShowBestDrivers: virtual stop missing in recommendation stops', {
      routeId,
      driverId
    });
    return {
      beforeTrailerCapacity: undefined,
      afterTrailerCapacity: fallbackMaxSkid
    };
  }

  const beforePickupTrailerSpaceRemaining =
    virtualStop[ESTIMATED_TRAILER_SPACE_BEFORE_PICKUP_ASSIGNMENT_FIELD];
  if (!beforePickupTrailerSpaceRemaining) {
    RMAXLogger.info('ShowBestDrivers: missing virtual stop field for before-assignment trailer space', {
      routeId,
      driverId,
      field: ESTIMATED_TRAILER_SPACE_BEFORE_PICKUP_ASSIGNMENT_FIELD
    });
  }

  return {
    beforeTrailerCapacity: beforePickupTrailerSpaceRemaining,
    afterTrailerCapacity: virtualStop.estimatedTrailerSpaceRemaining ?? fallbackMaxSkid
  };
};

const getFormattedRouteRecommendationData = ({
  recommendationRoutes,
  pickupsList,
  currentActiveRoutes,
  pickupId
}) => {
  const updatedRecommendationRoutes = recommendationRoutes.map((recommendedRoute) => {
    const matchedRoute = currentActiveRoutes.find(
      (route) => route.routeId?.toLowerCase() === recommendedRoute.routeId?.toLowerCase()
    );
    if (matchedRoute) {
      const { color, driverId, driverName, driverNumber, currentStatus } = matchedRoute;
      const { stops, ...remainingRecommendationInfo } = recommendedRoute;
      const routeStops = getFormattedRecommendationStopsData(stops, pickupId);
      const { beforeTrailerCapacity, afterTrailerCapacity } = getRecommendationTrailerCapacityDetails({
        stops,
        matchedRoute,
        routeId: remainingRecommendationInfo.routeId,
        driverId
      });
      const virtualStop = stops.find((stop) => stop.isVirtualStop);

      //Used to display recommended driver card data
      const driverRecommendationDetails = {
        color,
        driverId,
        driverName,
        driverNumber,
        currentStatus,
        routeStops: getStopsPropertiesForRecommendation(matchedRoute.stops, color),

        //recommendation properties
        trailerCapacity: afterTrailerCapacity,
        arrivalTime: virtualStop?.estimatedTimeOfArrival,
        ...remainingRecommendationInfo
      };

      //Before pickup assignment driver route preview
      const currentRoutePreview = getFormattedDriverCurrentRoutesInfo(matchedRoute);

      //After pickup assignment driver route preview
      const recommendedRoutePreview = getDriverAssignmentsList({
        pickupsList,
        currentActiveRoutes,
        driverAssignments: [
          { ...remainingRecommendationInfo, driverId, color, routeStops, pickupIds: [pickupId] }
        ] //pickupIds used here to get unassigned marker details in absence of driver routes
      });

      return {
        driverRecommendationDetails,
        //Used to display selected driver's route preview
        driverRoutePreview: {
          currentRoutePreview,
          recommendedRoutePreview,
          currentDriverRouteKPI: getCurrentDriverRouteKPI(
            currentRoutePreview,
            beforeTrailerCapacity
          ), //Before pickup assignment driver kpi
          recommendedDriverRouteKPI: getRecommendedDriverRouteKPI( //After pickup assignment driver kpi
            recommendedRoutePreview,
            {
              currentRouteStopCount: currentRoutePreview[0].stops?.length,
              currentArriveAtTerminalTime: currentRoutePreview[0].arriveAtTerminalTime,
              trailerCapacity: afterTrailerCapacity
            }
          )
        },
      };
    }
  });
  return {
    recommendationData: sortBy(
      updatedRecommendationRoutes.filter(Boolean),
      (route) => route.driverRecommendationDetails?.recommendationRank
    ),
    unassignedPickups: getSelectedUnassignPickupInfo(pickupsList, pickupId) //For showing selected unassigned marker
  };
};

const getCurrentDriverRouteKPI = ([driverRoute], trailerCapacity) => {
  const {
    stops,
    estimatedMilesRemaining,
    estimatedIncreaseInMiles,
    estimatedTravelTimeRemaining,
    estimatedIncreaseInTravelTime,
    arriveAtTerminalTime,
    numServiceViolations
  } = driverRoute || {};
  return {
    totalStops: stops.length || 0,
    estimatedMilesRemaining: getEstimatedMilesRemaining({
      estimatedMilesRemaining,
      estimatedIncreaseInMiles
    }),
    estimatedTravelTime: getEstimatedTravelTime({
      estimatedTravelTimeRemaining,
      estimatedIncreaseInTravelTime,
    }),
    palletsLeft: formatPalletsLeftKpi(trailerCapacity),
    arriveAtTerminalTime: getArriveAtTerminalTime(arriveAtTerminalTime),
    numServiceViolations,
  };
};

const getRecommendedDriverRouteKPI = (
  [recommendedDriverRoute],
  { currentRouteStopCount, currentArriveAtTerminalTime, trailerCapacity }
) => {
  const {
    stops,
    estimatedMilesRemaining,
    estimatedIncreaseInMiles,
    estimatedTravelTimeRemaining,
    estimatedIncreaseInTravelTime,
    arriveAtTerminalTime,
    numServiceViolations
  } = recommendedDriverRoute || {};
  return {
    totalStops: formatRecommendedStops(stops.length, currentRouteStopCount),
    estimatedMilesRemaining: getEstimatedMilesRemaining({
      estimatedMilesRemaining,
      estimatedIncreaseInMiles
    }),
    estimatedTravelTime: getEstimatedTravelTime({
      estimatedTravelTimeRemaining,
      estimatedIncreaseInTravelTime
    }),
    palletsLeft: formatPalletsLeftKpi(trailerCapacity),
    arriveAtTerminalTime: formatRecommendedArriveAtTerminalTime(
      currentArriveAtTerminalTime,
      arriveAtTerminalTime
    ),
    numServiceViolations
  };
};

const formatRecommendedStops = (recommendedRouteStopsCount, currentRouteStopCount = 0) => {
  const difference = recommendedRouteStopsCount - currentRouteStopCount;
  const differenceSign = difference > 0 ? '+' : '';
  const formattedStops =
    difference === 0
      ? `${recommendedRouteStopsCount}`
      : `${recommendedRouteStopsCount}(${differenceSign}${difference})`;
  return formattedStops;
};

const getFormattedDriverCurrentRoutesInfo = (driverCurrentRoute) => {
  const { stops } = driverCurrentRoute;
  const updatedStops = stops
    .filter((stop) => !stop.isStopCompleted)
    .map((stop) => ({
      ...stop,
      driverId: driverCurrentRoute.driverId,
      driverName: driverCurrentRoute.driverName,
      driverNumber: driverCurrentRoute.driverNumber,
      color: driverCurrentRoute.color
    }));
  return [{ ...driverCurrentRoute, stops: updatedStops }];
};

const getSelectedUnassignPickupInfo = (pickupsList, pickupId) => {
  const unassignPickupData = pickupsList.find(
    (pickup) => pickup.pickupId.toLowerCase() === pickupId.toLowerCase()
  );
  const selectPickupStopInfo = {
    isUnassignedPickup: true,
    ...unassignPickupData,
    latitude: unassignPickupData.shipperLatitude,
    longitude: unassignPickupData.shipperLongitude
  };
  return [selectPickupStopInfo];
};

const getFormattedRecommendationStopsData = (stops, pickupId) => {
  return stops.map((stop) => ({
    ...stop,
    pickupId
  }));
};

export { getFormattedRouteRecommendationData, getRecommendationTrailerCapacityDetails };
